import React, {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {Box, Theme} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import {PGTypography, getProductLineColors} from '@progynyglobal/pg-ui-library'
import {keyframes} from 'tss-react'
import {makeStyles} from 'tss-react/mui'

import illustration2 from '@progyny/assets/images/calendar-loader-illustration-2.png'
import illustration3 from '@progyny/assets/images/calendar-loader-illustration-3.png'
import illustration1 from '@progyny/assets/images/calendar-loader-illustration.png'

const SLIDE_DURATION_MS = 4000
const FADE_DURATION_MS = 800

const SLIDES = [
  {
    image: illustration1,
    titleKey: 'calendar_loader_title' as const,
    subtitleKey: 'calendar_loader_subtitle' as const,
  },
  {
    image: illustration2,
    titleKey: 'calendar_loader_variant2_title' as const,
    subtitleKey: 'calendar_loader_variant2_subtitle' as const,
  },
  {
    image: illustration3,
    titleKey: 'calendar_loader_variant3_title' as const,
    subtitleKey: 'calendar_loader_variant3_subtitle' as const,
  },
]

interface CalendarLoaderProps {
  open?: boolean
  productLine?: string
}

const CalendarLoader: React.FC<CalendarLoaderProps> = ({
  open = true,
  productLine,
}) => {
  const {t} = useTranslation()
  const theme = useTheme()
  const primaryColor =
    getProductLineColors(productLine)?.primary ?? theme.palette.primary.main
  const {classes} = useStyles({primaryColor})
  const [currentIndex, setCurrentIndex] = useState(0)
  const [visible, setVisible] = useState(true)

  useEffect(() => {
    if (!open) return

    let fadeTimeout: ReturnType<typeof setTimeout>

    const interval = setInterval(() => {
      setVisible(false)
      fadeTimeout = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % SLIDES.length)
        setVisible(true)
      }, FADE_DURATION_MS)
    }, SLIDE_DURATION_MS)

    return () => {
      clearInterval(interval)
      clearTimeout(fadeTimeout)
    }
  }, [open])

  const currentSlide = SLIDES[currentIndex]
  const currentTitle = t(currentSlide.titleKey)

  return (
    <Box className={classes.container}>
      <Box className={classes.progressTrack}>
        <Box key={currentIndex} className={classes.progressFill} />
      </Box>
      <Box className={classes.slideContent} style={{opacity: visible ? 1 : 0}}>
        <img
          src={currentSlide.image}
          alt={currentTitle}
          className={classes.illustration}
        />
        <Box className={classes.textSection}>
          <PGTypography variant='body' className={classes.title}>
            {currentTitle}
          </PGTypography>
          <PGTypography variant='body' className={classes.subtitle}>
            {t(currentSlide.subtitleKey)}
          </PGTypography>
        </Box>
      </Box>
      <Box className={classes.dotsContainer}>
        {SLIDES.map((_, index) => (
          <Box
            key={index}
            className={classes.dot}
            style={{
              backgroundColor:
                index === currentIndex ? primaryColor : theme.palette.grey[300],
            }}
          />
        ))}
      </Box>
    </Box>
  )
}

const progressGrow = keyframes`
  from { transform: scaleX(0); }
  to { transform: scaleX(1); }
`

const useStyles = makeStyles<{primaryColor: string}>()(
  (theme: Theme, {primaryColor}) => ({
    container: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '24px',
      width: '100%',
      height: '100%',
      minHeight: '500px',
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      borderRadius: '14px',
      overflow: 'hidden',
      padding: '32px',
    },
    dotsContainer: {
      display: 'flex',
      flexDirection: 'row',
      gap: '8px',
    },
    dot: {
      width: '8px',
      height: '8px',
      borderRadius: '50%',
      transition: 'background-color 300ms ease',
    },
    progressTrack: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '2px',
      overflow: 'hidden',
    },
    progressFill: {
      width: '100%',
      height: '100%',
      backgroundColor: primaryColor,
      transformOrigin: 'left center',
      animation: `${progressGrow} ${SLIDE_DURATION_MS + FADE_DURATION_MS}ms linear forwards`,
    },
    slideContent: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '32px',
      transition: `opacity ${FADE_DURATION_MS}ms ease-in-out`,
    },
    textSection: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '8px',
    },
    title: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      fontSize: '18px',
      lineHeight: '24px',
      textAlign: 'center',
      color: primaryColor,
      maxWidth: '288px',
    },
    subtitle: {
      fontFamily: 'DM Sans',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '20px',
      textAlign: 'center',
      color: theme.palette.text.secondary,
      maxWidth: '345px',
    },
    illustration: {
      width: '214px',
      height: '214px',
      objectFit: 'contain',
    },
  })
)

export default CalendarLoader
